import { useRef, useEffect, useCallback } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useSubmissionWizard } from '@/hooks/useSubmissionWizard'
import { useMultiUpload } from '@/hooks/useMultiUpload'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { MultiUploadStatusIndicator } from './UploadStatusIndicator'
import { NameStep } from './steps/NameStep'
import { EmailStep } from './steps/EmailStep'
import { GuidelinesStep } from './steps/GuidelinesStep'
import { GradeStep } from './steps/GradeStep'
import { PhotoStep } from './steps/PhotoStep'
import { PeopleCheckStep } from './steps/PeopleCheckStep'
import { TitleStep } from './steps/TitleStep'
import { ReviewStep } from './steps/ReviewStep'

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '50%' : '-50%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? '-50%' : '50%',
    opacity: 0,
  }),
}

export function SubmissionWizard() {
  const wizard = useSubmissionWizard()
  const upload = useMultiUpload()

  // Track the last uploaded photo to detect changes
  const lastUploadedPhotoRef = useRef<File | null>(null)

  // Handle photo step completion - trigger optimistic upload
  const handlePhotoStepNext = useCallback(() => {
    const { photo, email } = wizard.formData

    // Start upload if we have a photo and it's different from the last uploaded one
    if (photo && email && photo !== lastUploadedPhotoRef.current) {
      lastUploadedPhotoRef.current = photo
      upload.startUpload('photo', photo, email)
    }

    wizard.goNext()
  }, [wizard, upload])

  // Handle photo change - cancel existing upload if photo changes while uploading
  useEffect(() => {
    const currentPhoto = wizard.formData.photo
    const photoUpload = upload.getUpload('photo')

    // If photo changed and we have an ongoing upload, cancel it
    if (currentPhoto !== lastUploadedPhotoRef.current && photoUpload?.status === 'uploading') {
      upload.cancelUpload('photo')
      lastUploadedPhotoRef.current = null
    }
  }, [wizard.formData.photo, upload])

  // Sync successful photo upload s3Key back to formData for draft saving
  useEffect(() => {
    const photoUpload = upload.getUpload('photo')
    if (photoUpload?.status === 'success' && photoUpload.s3Key) {
      // Only update if different to avoid unnecessary re-renders
      if (wizard.formData.photoS3Key !== photoUpload.s3Key) {
        wizard.updateField('photoS3Key', photoUpload.s3Key)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on the specific wizard fields used; adding the whole `wizard` object (new ref each render) would re-run on every render
  }, [upload.state.uploads, wizard.formData.photoS3Key, wizard.updateField, upload])

  // Retry handler for failed photo upload
  const handleRetryPhotoUpload = useCallback(() => {
    const { photo, email } = wizard.formData
    if (photo && email) {
      upload.retryUpload('photo', photo, email)
    }
  }, [wizard.formData, upload])

  // Dismiss handler for upload notifications
  const handleDismissUpload = useCallback(
    (id: string) => {
      upload.dismissUpload(id)
    },
    [upload]
  )

  // Get all active uploads for the status indicator
  const allUploads = Object.values(upload.state.uploads)

  const renderStep = () => {
    const commonProps = {
      formData: wizard.formData,
      updateField: wizard.updateField,
      onNext: wizard.goNext,
      onBack: wizard.goBack,
      canGoBack: wizard.canGoBack,
    }

    switch (wizard.step) {
      case 'email':
        return (
          <EmailStep
            {...commonProps}
            loadDraftForEmail={wizard.loadDraftForEmail}
            clearDraft={wizard.clearDraft}
            isDraftLoading={wizard.isDraftLoading}
          />
        )
      case 'guidelines':
        return <GuidelinesStep {...commonProps} />
      case 'name':
        return <NameStep {...commonProps} />
      case 'grade':
        return <GradeStep {...commonProps} />
      case 'photo':
        return <PhotoStep {...commonProps} onNext={handlePhotoStepNext} />
      case 'peopleCheck':
        return <PeopleCheckStep {...commonProps} />
      case 'title':
        return <TitleStep {...commonProps} />
      case 'review':
        return (
          <ReviewStep
            formData={wizard.formData}
            onBack={wizard.goBack}
            goToStep={wizard.goToStep}
            uploadState={upload.state}
            onRetryPhotoUpload={handleRetryPhotoUpload}
          />
        )
      default:
        return null
    }
  }

  // Progress bar shows steps 1-8 (email through review)
  const progressStep = wizard.currentStep
  const progressTotal = wizard.totalSteps

  return (
    <div className="bg-background min-h-screen overflow-x-hidden">
      <ProgressBar currentStep={progressStep} totalSteps={progressTotal} />

      <div className="flex min-h-screen items-center justify-center overflow-x-hidden px-4 py-16">
        <AnimatePresence mode="wait" custom={wizard.direction}>
          <motion.div
            key={wizard.step}
            custom={wizard.direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.15 },
            }}
            className="w-full max-w-2xl"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Floating upload status indicator - hide on review step (has inline indicator) */}
      {wizard.step !== 'review' && (
        <MultiUploadStatusIndicator
          uploads={allUploads}
          onRetry={(id) => {
            if (id === 'photo') {
              handleRetryPhotoUpload()
            }
          }}
          onDismiss={handleDismissUpload}
        />
      )}
    </div>
  )
}
